import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/Dialog';
import { PauseIcon, PlayIcon } from '@/components/Icons';
import StyleFilterDetailCard from '@/components/StyleFilterDetailCard/StyleFilterDetailCard';
import { Button } from '@/components/ui/button';
import { useBuyStyleFilter } from '@/pages/StyleFilterMarket/hooks/useBuyStyleFilter';
import { FilterGroup } from '@/service/type/Filter';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { cn } from '@/utils';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ArtiestFilters from '../ArtiestFilters';
import StyleFilterDetailDialogContent from '../StyleFilterDetailDialogContent';
import { titleCaseToKebabCase } from '@/utils';
interface FilterCardProps {
  data: FilterGroup;
  className?: string;
}

export default function FilterCard({ data, className }: FilterCardProps) {
  const { play, playAudioInfo, isPlaying, pause, currentTime, stop } = useAudioPlayerStore();

  const { selectedValue, handleSwitch } = useBuyStyleFilter(data);

  const [open, setOpen] = useState<boolean>(false);
  const [openArtist, setOpenArtist] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navigateWithScroll = (path: string) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handlePlay = (filter: FilterGroup) => {
    stop();
    if (isPlaying && playAudioInfo?.id === String(filter.id)) {
      pause();
    } else {
      play({
        url: filter.audio,
        name: filter.name,
        id: String(filter.id),
      });
    }
  };

  return (
    <div className={cn('bg-[#fff] border border-[#202223]/10 rounded-xl p-6 flex-shrink-0', className)}>
      <StyleFilterDetailCard
        onRemove={() => undefined}
        onUse={() => undefined}
        title={data?.artist}
        content={data?.name}
        onSwitch={handleSwitch}
        defaultValue={selectedValue === 0 ? 'LOW' : 'HIGH'}
        sfId={data?.id}
        background={data?.album_cover}
        showButtons={false}
        disabled={true}
        switchDisabled={true}
        fontSize={9}
        isMarket={true}
        innerClassName="p-[4px]"
        coverStyle={{
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          height: '272px',
          width: '167px',
          marginBottom: '25px',
          position: 'relative',
          zIndex: 1,
          boxShadow: `
            50.667px 80px 26.667px 0px rgba(0, 0, 0, 0.00), 
            32px 52px 24px 0px rgba(0, 0, 0, 0.03), 
            18.667px 29.333px 20px 0px rgba(0, 0, 0, 0.09), 
            8px 13.333px 14.667px 0px rgba(0, 0, 0, 0.16), 
            2.667px 2.667px 8px 0px rgba(0, 0, 0, 0.19)
          `,
        }}
      />
      {/* Track details */}
      <div className="">
        <div>
          <h2
            className="text-[1rem] text-neutral-500 cursor-pointer hover:text-jen-orange transition-colors tracking-[-0.32px]"
            onClick={() => {
              navigateWithScroll(`/marketplace/artist/${titleCaseToKebabCase(data?.artist)}`);
            }}
          >
            Imogen Heap
          </h2>
          <h1
            className="text-[1.25rem] mt-[6px] leading-[1.25rem] cursor-pointer hover:text-jen-orange transition-colors tracking-[-0.32px]"
            onClick={() => {
              navigateWithScroll(`/marketplace/filter/${titleCaseToKebabCase(data?.name)}`);
            }}
          >
            {data?.name}
          </h1>
        </div>

        <div className="flex items-center gap-2 text-neutral-500">
          <span className="text-neutral-500 text-[0.75rem] tracking-[-1%] mt-[18px]">
            From $4.99 for 90 Min of Low Jeneration or $7.99 for 90 Min of High Jeneration
          </span>
        </div>

        <div className="flex items-center justify-between mt-3">
          <Button
            variant="ghost"
            className="bg-jen-orange hover:bg-jen-orange/80 text-white hover:text-white px-4"
            onClick={() => {
              navigateWithScroll(`/marketplace/filter/${titleCaseToKebabCase(data?.name)}`);
            }}
          >
            Buy Now
          </Button>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              rounded
              className="h-10 w-10 bg-jen-orange-10 text-jen-orange relative z-10"
              onClick={() => handlePlay(data)}
            >
              {isPlaying && playAudioInfo?.id === String(data.id) ? <PauseIcon fillColor="#FF5B27" /> : <PlayIcon fillColor="#FF5B27" />}
            </Button>
            {isPlaying && playAudioInfo?.id === String(data.id) && (
              <svg className="absolute top-0 left-0 w-10 h-10 -rotate-90" viewBox="0 0 40 40">
                <circle
                  cx="20"
                  cy="20"
                  r="18"
                  fill="none"
                  stroke="#FF5B27"
                  strokeWidth="2"
                  strokeDasharray={`${(currentTime / data.audio_duration) * (2 * 3.14 * 20)} ${2 * 3.14 * 20}`}
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* StyleFilter Detail Dialog */}
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setOpen(false);
          } else {
            setOpen(true);
          }
        }}
      >
        <DialogContent
          className="max-w-[90%] w-[1200px] max-h-[90vh] overflow-y-auto"
          closePosition="right"
          closeStyle={{
            borderRadius: '50%',
            backgroundColor: '#e3e3e3',
          }}
        >
          <DialogHeader className="sticky top-0 bg-white z-10">
            <DialogTitle>{data?.name}</DialogTitle>
          </DialogHeader>
          <StyleFilterDetailDialogContent id={data?.id} />
        </DialogContent>
      </Dialog>

      {/* StyleFilter Artist Detail Dialog */}
      <Dialog
        open={openArtist}
        onOpenChange={(openArtist) => {
          if (!openArtist) {
            setOpenArtist(false);
          } else {
            setOpenArtist(true);
          }
        }}
      >
        <DialogContent
          className="max-w-[90%] w-[1200px] max-h-[90vh] overflow-y-auto"
          closePosition="right"
          closeStyle={{
            borderRadius: '50%',
            backgroundColor: '#e3e3e3',
          }}
        >
          <DialogHeader className="sticky top-0 bg-white z-10">
            <DialogTitle>{data?.name}</DialogTitle>
          </DialogHeader>
          <ArtiestFilters />
        </DialogContent>
      </Dialog>
    </div>
  );
}
